import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Alert,
  Snackbar
} from '@mui/material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { Settings as SettingsIcon, Save, Refresh } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import settingsService from '../services/settingsService.js';
import { settingsConfig } from '../config/settingsConfig.js';

const Settings = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const loadedSettings = settingsService.getAllSettings();
    setSettings(loadedSettings);
  }, []);

  // Create validation schema from config
  const createValidationSchema = () => {
    const schema = {};
    Object.values(settingsConfig).forEach(setting => {
      let fieldSchema = Yup.mixed();
      
      if (setting.validation.required) {
        fieldSchema = fieldSchema.required(`${setting.description} is required`);
      }
      
      if (setting.type === 'string') {
        fieldSchema = Yup.string();
        if (setting.validation.oneOf) {
          fieldSchema = fieldSchema.oneOf(setting.validation.oneOf, 'Invalid selection');
        }
      } else if (setting.type === 'number') {
        fieldSchema = Yup.number();
        if (setting.validation.min !== undefined) {
          fieldSchema = fieldSchema.min(setting.validation.min, `Must be at least ${setting.validation.min}`);
        }
        if (setting.validation.max !== undefined) {
          fieldSchema = fieldSchema.max(setting.validation.max, `Must be at most ${setting.validation.max}`);
        }
      } else if (setting.type === 'boolean') {
        fieldSchema = Yup.boolean();
      }
      
      schema[setting.key] = fieldSchema;
    });
    return Yup.object().shape(schema);
  };

  const handleSave = async (values, { setSubmitting }) => {
    try {
      const success = settingsService.saveAllSettings(values);
      if (success) {
        setSettings(values);
        
        // Clear wax tracking cache to force fresh data with new settings
        localStorage.removeItem('wax_tracking_cache');
        localStorage.removeItem('last_wax_date');
        console.log('Settings changed: cleared wax tracking cache');
        
        setSnackbar({
          open: true,
          message: 'Settings saved successfully!',
          severity: 'success'
        });
        // Navigate back to homepage after successful save
        setTimeout(() => {
          navigate('/');
        }, 1000);
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to save settings',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setSnackbar({
        open: true,
        message: 'Error saving settings',
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    const defaults = settingsService.resetSettings();
    if (defaults) {
      const loadedSettings = settingsService.getAllSettings();
      setSettings(loadedSettings);
      setSnackbar({
        open: true,
        message: 'Settings reset to defaults',
        severity: 'info'
      });
    }
  };

  const renderField = (setting) => {
    const { key, description, type, validation } = setting;
    const value = settings[key];

    switch (type) {
      case 'boolean':
        return (
          <FormControlLabel
            key={key}
            control={
              <Field
                name={key}
                as={Switch}
                color="primary"
              />
            }
            label={description}
          />
        );

      case 'string':
        if (validation.oneOf) {
          return (
            <FormControl key={key} fullWidth margin="normal">
              <InputLabel>{description}</InputLabel>
              <Field
                name={key}
                as={Select}
                label={description}
              >
                {validation.oneOf.map(option => (
                  <MenuItem key={option} value={option}>
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </MenuItem>
                ))}
              </Field>
            </FormControl>
          );
        }
        return (
          <Field
            key={key}
            name={key}
            as={TextField}
            label={description}
            fullWidth
            margin="normal"
          />
        );

      case 'number':
        return (
          <Field
            key={key}
            name={key}
            as={TextField}
            label={description}
            type="number"
            fullWidth
            margin="normal"
            inputProps={{
              min: validation.min,
              max: validation.max
            }}
          />
        );

      default:
        return null;
    }
  };

  if (Object.keys(settings).length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading settings...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <SettingsIcon sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant="h5">Settings</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Configure your Whacks Off settings! All settings are stored locally on your device.
        </Typography>

        <Formik
          initialValues={settings}
          validationSchema={createValidationSchema()}
          onSubmit={handleSave}
          enableReinitialize
        >
          {({ isSubmitting, errors, touched }) => (
            <Form>
              {renderField(settingsConfig.waxIntervalDistance)}
              {renderField(settingsConfig.matchPattern)}
              {renderField(settingsConfig.gearId)}
              
              {/* Action Buttons */}
              <Box display="flex" gap={2} mt={4}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<Save />}
                  disabled={isSubmitting}
                >
                  Save Settings
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={handleReset}
                >
                  Reset to Defaults
                </Button>
              </Box>
              
              {/* Show validation errors */}
              {Object.keys(errors).length > 0 && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  Please fix the following errors:
                  <ul>
                    {Object.entries(errors).map(([key, error]) => (
                      <li key={key}>{error}</li>
                    ))}
                  </ul>
                </Alert>
              )}
            </Form>
          )}
        </Formik>
      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;
